import React from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import {
  NoMoneyIcon,
  PaymentSpeedIcon,
  VehicleLorryIcon,
  VerifiedIcon,
} from '@motorway/mw-highway-code';

import type { ServiceFeeTiersResponse } from 'Components/transactionFees/hooks/useServiceFeeData';

import { AgentIcon } from '../icons/AgentIcon';
import { PaymentSentIcon } from '../icons/PaymentSentIcon';
import { VectorIcon } from '../icons/VectorIcon';
import Texts from '../transactionFees.texts.json';

import styles from './ServiceFeeExplainer.scss';

const LocalT = new MDText(Texts);

const CONDITIONS = [
  { icon: NoMoneyIcon, key: 'noSaleNoFee' },
  { icon: PaymentSpeedIcon, key: 'fastPayment' },
  { icon: VehicleLorryIcon, key: 'freeCollection' },
  { icon: VerifiedIcon, key: 'verifiedDealer' },
  { icon: VectorIcon, key: 'dealerNetwork' },
  { icon: PaymentSentIcon, key: 'financeSettlement' },
  { icon: PaymentSentIcon, key: 'refundPolicy' },
  { icon: AgentIcon, key: 'customerService' },
] as const;

type ServiceFeeExplainerProps = {
  serviceFeeTiers?: ServiceFeeTiersResponse;
};

export const ServiceFeeExplainer: React.FC<ServiceFeeExplainerProps> = ({ serviceFeeTiers }) => {
  const tiers = serviceFeeTiers?.tiers ?? [];

  return (
    <div className={styles.explainer} data-testid="transaction-fee-explainer">
      <p className={styles.heading}>
        {LocalT.translate('serviceFeeExplainer.heading')}
      </p>
      <p className={styles.description}>
        {LocalT.translate('serviceFeeExplainer.description')}
      </p>
      <div className={styles.conditionList}>
        {CONDITIONS.map(({ icon: Icon, key }) => (
          <div key={key} className={styles.conditionItem}>
            <div className={styles.conditionIcon}>
              <Icon filled={false} size="medium" />
            </div>
            <div className={styles.conditionContent}>
              <span className={styles.conditionTitle}>
                {LocalT.translate(`serviceFeeExplainer.conditions.${key}.title`)}
              </span>
              <span className={styles.conditionDescription}>
                {LocalT.translate(`serviceFeeExplainer.conditions.${key}.description`)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {tiers.length > 0 && (
        <>
          <section className={styles.tierTableSection}>
            <span className={styles.header}>{LocalT.translate('serviceFeeExplainer.tierTableSection.header') as string}</span>
            <span className={styles.description}>{LocalT.translate('serviceFeeExplainer.tierTableSection.description') as string}</span>
          </section>
          <section className={styles.tierTable} data-testid="service-fee-tier-table">
            <div key={'header'} className={cx(styles.tierRow, styles.tierHeader)} data-testid='tier-row-header'>
              <span>{LocalT.translate('serviceFeeExplainer.tierTable.header') as string}</span>
              <span>{LocalT.translate('serviceFeeExplainer.tierTable.value') as string}</span>
            </div>
            {tiers.map((tier, index) => {
              let priceRange: string;
              if (index === 0) {
                priceRange = `Up to £${tier.maxPrice!.toLocaleString()}`;
              } else if (tier.maxPrice === undefined) {
                priceRange = `£${tier.minPrice.toLocaleString()} and over`;
              } else {
                priceRange = `£${tier.minPrice.toLocaleString()} to £${(tier.maxPrice - 1).toLocaleString()}`;
              }
              const fee = tier.feeGBP === 0
                ? LocalT.translate('serviceFeeExplainer.tierTable.free') as string
                : `£${tier.feeGBP.toLocaleString()}`;
              return (
                <section key={tier.tier}>
                  { index !== 0 && (<div className={styles.spacer} />) }
                  <div className={styles.tierRow} data-testid={`tier-row-${tier.tier}`}>
                    <span className={styles.tierCell}>{priceRange}</span>
                    <span className={styles.tierCell}>{fee}</span>
                  </div>
                </section>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
};
